import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { supabase } from '@/lib/supabase';
import CampoTexto from '@/components/base/CampoTexto';
import CampoArea from '@/components/base/CampoArea';
import { formatarValor } from '@/pages/evento/utils';
import { mascaraCNPJ, mensagemErro } from '../utils';
import type { ComissaoInfo, SorteioInfo } from '../types';

interface EspelhoRifaProps {
  sorteio: SorteioInfo;
  comissao: ComissaoInfo | null;
  nomeEvento: string;
  onPublicou: () => void;
}

interface CamposEditaveis {
  entidade_nome: string;
  entidade_cnpj: string;
  autorizacao_spa: string;
  regulamento: string;
  sorteio_onde: string;
  max_por_compra: string;
}

interface PremioLinha {
  descricao: string;
  imagem_url: string | null;
  doado_por: string | null;
}

const CAMPOS_VAZIOS: CamposEditaveis = {
  entidade_nome: '',
  entidade_cnpj: '',
  autorizacao_spa: '',
  regulamento: '',
  sorteio_onde: '',
  max_por_compra: '',
};

export default function EspelhoRifa({
  sorteio,
  comissao,
  nomeEvento,
  onPublicou,
}: EspelhoRifaProps) {
  const [campos, setCampos] = useState<CamposEditaveis>(CAMPOS_VAZIOS);
  const [originais, setOriginais] = useState<CamposEditaveis>(CAMPOS_VAZIOS);
  const [sorteioPor, setSorteioPor] = useState('');

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [avisoSalvo, setAvisoSalvo] = useState(false);
  const [campoErroEntidade, setCampoErroEntidade] = useState(false);
  const [recadoPublicacao, setRecadoPublicacao] = useState<string | null>(null);
  const [avisoAmarelo, setAvisoAmarelo] = useState<string | null>(null);

  const [premios, setPremios] = useState<PremioLinha[]>([]);
  const [mostrandoPremios, setMostrandoPremios] = useState(false);

  const [premioPrincipal, setPremioPrincipal] = useState('');
  const [fotoAtual, setFotoAtual] = useState<string | null>(null);
  const [subindoFoto, setSubindoFoto] = useState(false);
  const [erroFoto, setErroFoto] = useState<string | null>(null);
  const [avisoFoto, setAvisoFoto] = useState(false);
  const inputFotoRef = useRef<HTMLInputElement>(null);

  const [confirmandoPublicar, setConfirmandoPublicar] = useState(false);
  const [publicando, setPublicando] = useState(false);

  const numeroCentavos = Math.round((comissao?.numero_reais ?? 0) * 100);
  const valorAbateCentavos = sorteio.por_talao * numeroCentavos;
  const fmt = (n: number) => n.toLocaleString('pt-BR');
  const mostraSorteioOnde = sorteioPor === 'instagram' || sorteioPor === 'facebook';

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      const { data, error } = await supabase
        .from('rifas')
        .select(
          'entidade_nome, entidade_cnpj, autorizacao_spa, regulamento, sorteio_onde, sorteio_por, max_cotas_por_compra, premio, premio_imagem_url',
        )
        .eq('id', sorteio.id)
        .maybeSingle();
      if (!ativo) return;
      if (error || !data) {
        setCarregando(false);
        return;
      }
      const r = data as Record<string, unknown>;
      const c: CamposEditaveis = {
        entidade_nome: (r.entidade_nome as string) ?? '',
        entidade_cnpj: (r.entidade_cnpj as string) ?? '',
        autorizacao_spa: (r.autorizacao_spa as string) ?? '',
        regulamento: (r.regulamento as string) ?? '',
        sorteio_onde: (r.sorteio_onde as string) ?? '',
        max_por_compra: r.max_cotas_por_compra ? String(r.max_cotas_por_compra) : '',
      };
      setCampos(c);
      setOriginais(c);
      setSorteioPor((r.sorteio_por as string) ?? '');
      setPremioPrincipal((r.premio as string) ?? '');
      setFotoAtual(((r.premio_imagem_url as string) ?? '').trim() || null);
      setCarregando(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [sorteio.id]);

  function setCampo(k: keyof CamposEditaveis, v: string) {
    setCampos((c) => ({ ...c, [k]: v }));
    setAvisoSalvo(false);
  }

  function houveMudanca(): boolean {
    return (Object.keys(campos) as (keyof CamposEditaveis)[]).some(
      (k) => campos[k] !== originais[k],
    );
  }

  async function salvar(): Promise<boolean> {
    setSalvando(true);
    setErro(null);
    setAvisoSalvo(false);
    try {
      const p: Record<string, unknown> = {};
      if (campos.entidade_nome !== originais.entidade_nome) {
        p.entidade_nome = campos.entidade_nome.trim();
      }
      if (campos.entidade_cnpj !== originais.entidade_cnpj) {
        p.entidade_cnpj = campos.entidade_cnpj.trim();
      }
      if (campos.autorizacao_spa !== originais.autorizacao_spa) {
        p.autorizacao_spa = campos.autorizacao_spa.trim();
      }
      if (campos.regulamento !== originais.regulamento) {
        p.regulamento = campos.regulamento.trim();
      }
      if (campos.sorteio_onde !== originais.sorteio_onde) {
        p.sorteio_onde = campos.sorteio_onde.trim();
      }
      if (campos.max_por_compra !== originais.max_por_compra) {
        const n = Number(campos.max_por_compra);
        if (campos.max_por_compra.trim() !== '' && Number.isFinite(n) && n > 0) {
          p.max_por_compra = n;
        }
      }

      if (Object.keys(p).length === 0) return true;

      const { error } = await supabase.rpc('ajustar_rifa', { p_rifa: sorteio.id, p });
      if (error) throw error;

      setOriginais(campos);
      setAvisoSalvo(true);
      window.setTimeout(() => setAvisoSalvo(false), 2500);
      return true;
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível salvar. Tente novamente.'));
      return false;
    } finally {
      setSalvando(false);
    }
  }

  async function verPremios() {
    if (mostrandoPremios) {
      setMostrandoPremios(false);
      return;
    }
    setMostrandoPremios(true);
    if (premios.length > 0) return;
    const { data } = await supabase
      .from('rifa_premios')
      .select('descricao, imagem_url, doado_por')
      .eq('rifa_id', sorteio.id)
      .order('ordem');
    setPremios((data as PremioLinha[]) || []);
  }

  function abrirSeletorFoto() {
    inputFotoRef.current?.click();
  }

  async function aoEscolherFoto(e: ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!arquivo) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(arquivo.type)) {
      setErroFoto('Use uma imagem jpg, png ou webp.');
      return;
    }
    if (arquivo.size > 5 * 1024 * 1024) {
      setErroFoto('Esta imagem tem mais de 5 MB. Escolha uma menor.');
      return;
    }
    setErroFoto(null);
    setAvisoFoto(false);
    setSubindoFoto(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const uid = session?.user?.id || 'anonimo';
      const ext = (arquivo.name.split('.').pop() || 'jpg').toLowerCase();
      const caminho = `${uid}/premio-${Date.now()}.${ext}`;
      const { error: errUp } = await supabase.storage.from('capas').upload(caminho, arquivo);
      if (errUp) throw errUp;
      const { data: pub } = supabase.storage.from('capas').getPublicUrl(caminho);
      const url = pub.publicUrl;

      const { error: errAjuste } = await supabase.rpc('ajustar_rifa', {
        p_rifa: sorteio.id,
        p: { premio_imagem_url: url },
      });
      if (errAjuste) throw errAjuste;

      setFotoAtual(url);
      setAvisoFoto(true);
      window.setTimeout(() => setAvisoFoto(false), 2500);
    } catch (err) {
      setErroFoto(
        mensagemErro(err, 'Não foi possível trocar a foto. Tente novamente.'),
      );
    } finally {
      setSubindoFoto(false);
    }
  }

  function aoAbrirVenda() {
    console.log('CLIQUE ABRIR', sorteio.id);
    setErro(null);
    setRecadoPublicacao(null);
    setAvisoAmarelo(null);
    if (!campos.entidade_nome.trim()) {
      setErro(
        'Antes de abrir a venda, diga qual entidade é responsável: a paróquia, a pastoral, o acampamento. É o campo que diz quem está promovendo, e ele aparece no regulamento.',
      );
      setCampoErroEntidade(true);
      window.setTimeout(() => {
        document
          .getElementById('entidade-nome')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setCampoErroEntidade(false);
    setConfirmandoPublicar(true);
  }

  async function publicar() {
    console.log('CLIQUE', sorteio.id);
    setPublicando(true);
    setErro(null);
    setCampoErroEntidade(false);
    setRecadoPublicacao(null);
    setAvisoAmarelo(null);
    try {
      const { data, error } = await supabase.rpc('abrir_a_venda', {
        p_rifa: sorteio.id,
        p: {
          entidade_nome: campos.entidade_nome.trim(),
          entidade_cnpj: campos.entidade_cnpj.trim() || null,
          autorizacao_spa: campos.autorizacao_spa.trim() || null,
        },
      });

      if (error) {
        setErro(mensagemErro(error, 'Não foi possível abrir a venda. Tente novamente.'));
        return;
      }

      const r = data as {
        ok: boolean;
        recado?: string;
        erro?: string;
        falta?: string;
        sem_autorizacao?: boolean;
        aviso?: string | null;
      } | null;

      if (!r || !r.ok) {
        setErro(mensagemErro(r?.erro, 'Não foi possível abrir a venda. Tente novamente.'));
        if (r?.falta === 'entidade_nome') {
          setCampoErroEntidade(true);
          setConfirmandoPublicar(false);
          window.setTimeout(() => {
            document
              .getElementById('entidade-nome')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 50);
        }
        return;
      }

      setConfirmandoPublicar(false);
      await supabase.rpc('repartir_taloes', { p_rifa: sorteio.id });
      setRecadoPublicacao(r.recado || 'A venda foi aberta.');
      if (r.sem_autorizacao && r.aviso) setAvisoAmarelo(r.aviso);
      onPublicou();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível abrir a venda. Tente novamente.'));
    } finally {
      setPublicando(false);
    }
  }

  if (carregando) {
    return (
      <div className="space-y-8">
        <div>
          <div className="h-7 w-64 animate-pulse rounded bg-secondary-100" />
          <div className="mt-2 h-4 w-80 animate-pulse rounded bg-secondary-100" />
        </div>
        <div className="h-56 w-full animate-pulse rounded-lg bg-secondary-100" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-normal text-foreground-950">
          Ação entre amigos — {nomeEvento.trim() || 'este evento'}
        </h2>
        <p className="mt-1 text-sm text-secondary-500">
          Cada inscrito ganha um talão. O que ele vende abate a inscrição dele.
        </p>
      </div>

      {erro && (
        <p className="rounded-md bg-danger-50 px-4 py-3 text-sm text-danger-700" data-erro="true">
          {erro}
        </p>
      )}

      {avisoSalvo && (
        <p className="flex items-center gap-1.5 rounded-md bg-green-50 px-4 py-3 text-sm font-medium text-green-700">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-checkbox-circle-line" />
          </span>
          Alterações salvas
        </p>
      )}

      {recadoPublicacao && (
        <p className="flex items-start gap-1.5 rounded-md bg-green-50 px-4 py-3 text-sm font-medium text-green-700">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-checkbox-circle-line" />
          </span>
          {recadoPublicacao}
        </p>
      )}

      {avisoAmarelo && (
        <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-700">
            <i className="ri-alert-line text-lg" />
          </span>
          {avisoAmarelo}
        </p>
      )}

      {/* Foto do prêmio */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <h3 className="text-base font-medium text-foreground-950">Foto do prêmio</h3>
        <p className="mt-1 text-sm text-secondary-500">
          É esta foto que aparece quando alguém abre a ação entre amigos no celular.
        </p>

        <div className="mt-4 flex items-center gap-4">
          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-secondary-200 bg-background-100">
            {fotoAtual ? (
              <img
                src={fotoAtual}
                alt="Foto do prêmio"
                className="h-full w-full object-cover object-top"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-secondary-400">
                <i className="ri-image-line text-2xl" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-[15px] text-foreground-900">
              {premioPrincipal || 'Sem prêmio definido'}
            </p>
            <button
              type="button"
              onClick={abrirSeletorFoto}
              disabled={subindoFoto}
              className="mt-2 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {subindoFoto ? 'Enviando…' : 'Trocar foto'}
            </button>
          </div>
        </div>

        <input
          ref={inputFotoRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          className="sr-only"
          onChange={aoEscolherFoto}
        />

        {erroFoto && (
          <p className="mt-3 text-sm text-danger-600" role="alert">
            {erroFoto}
          </p>
        )}
        {avisoFoto && (
          <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-green-700">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-checkbox-circle-line" />
            </span>
            Foto atualizada
          </p>
        )}
      </section>

      {/* O sorteio — números que não mudam mais */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <p className="flex items-center gap-2 text-base font-medium text-foreground-950">
          <span className="flex h-5 w-5 items-center justify-center text-green-600">
            <i className="ri-checkbox-circle-line text-lg" />
          </span>
          Criada e pronta
        </p>

        <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-secondary-400">
              Números
            </p>
            <p className="mt-0.5 text-[15px] text-foreground-900">
              {fmt(sorteio.numeros)}{' '}
              {numeroCentavos > 0 ? `de ${formatarValor(numeroCentavos)}` : ''}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-secondary-400">
              Números por talão
            </p>
            <p className="mt-0.5 text-[15px] text-foreground-900">{fmt(sorteio.por_talao)}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-secondary-400">
              Valor de cada número
            </p>
            <p className="mt-0.5 text-[15px] text-foreground-900">
              {numeroCentavos > 0 ? formatarValor(numeroCentavos) : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-secondary-400">
              Talões
            </p>
            <p className="mt-0.5 text-[15px] text-foreground-900">
              {fmt(sorteio.taloes)} — um para cada inscrito
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-secondary-400">
              Números vendidos
            </p>
            <p className="mt-0.5 text-[15px] text-foreground-900">{fmt(sorteio.vendidos)}</p>
          </div>
        </div>

        <p className="mt-4 text-xs text-secondary-400">
          Quantidade de números, números por talão e valor não dão para mudar depois de criado.
        </p>

        {numeroCentavos > 0 && sorteio.por_talao > 0 && (
          <p className="mt-3 rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-800">
            Cada talão inteiro abate {formatarValor(valorAbateCentavos)} da inscrição.
          </p>
        )}
      </section>

      {/* Para colocar no ar */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <h3 className="text-base font-medium text-foreground-950">Para colocar no ar</h3>
        <p className="mt-1 text-sm text-secondary-500">
          A entidade responsável é obrigatória para abrir a venda. CNPJ e autorização são
          opcionais.
        </p>

        <div className="mt-4 space-y-5">
          <CampoTexto
            id="entidade-nome"
            name="entidade_nome"
            label="Entidade responsável"
            value={campos.entidade_nome}
            onChange={(v) => {
              setCampo('entidade_nome', v);
              if (campoErroEntidade) setCampoErroEntidade(false);
            }}
            required
            error={campoErroEntidade ? 'Informe a entidade responsável para abrir a venda.' : null}
            placeholder="Paróquia São José"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CampoTexto
              id="entidade-cnpj"
              name="entidade_cnpj"
              label="CNPJ"
              help="Opcional."
              value={campos.entidade_cnpj}
              onChange={(v) => setCampo('entidade_cnpj', mascaraCNPJ(v))}
              inputMode="numeric"
              placeholder="00.000.000/0000-00"
            />
            <CampoTexto
              id="autorizacao-spa"
              name="autorizacao_spa"
              label="Nº da autorização (SPA/Ministério da Fazenda)"
              help="Opcional."
              value={campos.autorizacao_spa}
              onChange={(v) => setCampo('autorizacao_spa', v)}
              placeholder="0000000/0000"
            />
          </div>
        </div>
      </section>

      {/* Outros ajustes */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <h3 className="text-base font-medium text-foreground-950">Outros ajustes</h3>
        <div className="mt-4 space-y-5">
          <CampoArea
            id="regulamento"
            name="regulamento"
            label="Regulamento"
            help="Texto livre que aparece na página da ação entre amigos."
            value={campos.regulamento}
            onChange={(v) => setCampo('regulamento', v)}
          />
          {mostraSorteioOnde && (
            <CampoTexto
              id="sorteio-onde"
              name="sorteio_onde"
              label={sorteioPor === 'instagram' ? '@ do perfil' : '@ da página'}
              value={campos.sorteio_onde}
              onChange={(v) => setCampo('sorteio_onde', v)}
              placeholder="@nomeperfil"
            />
          )}
          <div className="max-w-[280px]">
            <CampoTexto
              id="max-por-compra"
              name="max_por_compra"
              label="Máximo de números por compra"
              value={campos.max_por_compra}
              onChange={(v) => setCampo('max_por_compra', v)}
              inputMode="numeric"
              placeholder="10"
            />
          </div>
        </div>

        {houveMudanca() && (
          <div className="mt-5 flex items-center gap-2">
            <button
              type="button"
              disabled={salvando}
              onClick={() => salvar()}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {salvando ? 'Salvando…' : 'Salvar alterações'}
            </button>
            <button
              type="button"
              disabled={salvando}
              onClick={() => {
                setCampos(originais);
                setAvisoSalvo(false);
              }}
              className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Desfazer
            </button>
          </div>
        )}
      </section>

      {/* A venda */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <h3 className="text-base font-medium text-foreground-950">A venda</h3>

        {sorteio.status === 'publicada' ? (
          <p className="mt-3 flex items-start gap-2 rounded-md bg-green-50 px-3 py-2.5 text-sm text-green-800">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-green-600">
              <i className="ri-check-line text-lg" />
            </span>
            A venda está aberta.
          </p>
        ) : confirmandoPublicar ? (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
            <p className="flex items-start gap-2 text-sm text-amber-900">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-700">
                <i className="ri-alert-line text-lg" />
              </span>
              Depois disso qualquer pessoa com o QR do talão consegue comprar. Não tem meio termo.
            </p>
            {erro && (
              <p
                className="mt-3 rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700"
                data-erro="true"
              >
                {erro}
              </p>
            )}
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                disabled={publicando}
                onClick={publicar}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {publicando ? 'Publicando…' : 'Publicar a venda'}
              </button>
              <button
                type="button"
                disabled={publicando}
                onClick={() => setConfirmandoPublicar(false)}
                className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-700">
                <i className="ri-information-line text-lg" />
              </span>
              Os talões já existem, mas ninguém consegue comprar até você abrir a venda.
            </p>
            <button
              type="button"
              disabled={salvando}
              onClick={aoAbrirVenda}
              className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {salvando ? 'Salvando…' : 'Abrir a venda'}
            </button>
          </>
        )}
      </section>

      {/* Prêmios */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <button
          type="button"
          onClick={verPremios}
          className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
        >
          {mostrandoPremios ? 'Esconder os prêmios' : 'Ver os prêmios'}
        </button>

        {mostrandoPremios &&
          (premios.length > 0 ? (
            <ul className="mt-4 space-y-3">
              {premios.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-secondary-100">
                    {p.imagem_url ? (
                      <img
                        src={p.imagem_url}
                        alt={`Prêmio ${i + 1}`}
                        className="h-full w-full object-cover object-top"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center px-1 text-center text-xs text-secondary-400">
                        sem foto ainda
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[15px] text-foreground-900">{p.descricao}</p>
                    {p.doado_por && (
                      <p className="text-sm text-secondary-500">Doado por {p.doado_por}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-secondary-500">Ainda não há prêmios cadastrados.</p>
          ))}
      </section>
    </div>
  );
}